import { Button, Spinner } from "@heroui/react"
import QRCode from "qrcode"
import { useEffect, useState } from "react"
import { bayarQris, statusPesanan } from "./api"
import { Berhasil, JudulPusat, Memuat, Pusat } from "./bagian"

const rupiah = (n: number) => `Rp${n.toLocaleString("id-ID")}`
/* Jaring pengaman kalau webhook telat: server ikut reconcile ke Doku tiap kali status ditarik. */
const SELANG_MS = 4000

type Fase = "memuat" | "tampil" | "lunas" | "galat"

/**
 * Layar bayar QRIS untuk tamu. Uang dan harga milik server: minta QRIS ke `/order/{id}/pay` (nominal
 * dihitung server), tampilkan kodenya, lalu POLLING `/order/{id}` sampai `paid`. Tamu tak bisa
 * menyatakan lunas sendiri — status datang dari webhook Doku atau reconcile `checkStatus` di server.
 */
export function Bayar({
  outletId,
  token,
  orderId,
  onSelesai,
}: {
  outletId: string
  token: string
  orderId: string
  onSelesai: () => void
}) {
  const [fase, setFase] = useState<Fase>("memuat")
  const [qrImg, setQrImg] = useState<string | null>(null)
  const [nominal, setNominal] = useState(0)
  const [galat, setGalat] = useState<string | null>(null)

  // Minta QRIS sekali. Kalau ternyata sudah lunas (idempoten), langsung ke layar lunas.
  useEffect(() => {
    let batal = false
    ;(async () => {
      try {
        const r = await bayarQris(outletId, token, orderId)
        if (batal) return
        if (r.status === "captured") {
          setFase("lunas")
          return
        }
        setNominal(r.amount)
        if (!r.qrContent) {
          setGalat("Server tidak mengembalikan kode QR")
          setFase("galat")
          return
        }
        const img = await QRCode.toDataURL(r.qrContent, { width: 264, margin: 1 })
        if (!batal) {
          setQrImg(img)
          setFase("tampil")
        }
      } catch (e) {
        if (!batal) {
          setGalat((e as Error).message)
          setFase("galat")
        }
      }
    })()
    return () => {
      batal = true
    }
  }, [outletId, token, orderId])

  // Polling status sampai lunas (hanya saat QR tampil).
  useEffect(() => {
    if (fase !== "tampil") return
    let batal = false
    const cek = async () => {
      try {
        const s = await statusPesanan(outletId, token, orderId)
        if (!batal && s.status === "paid") setFase("lunas")
      } catch {
        /* abaikan sekali gagal — coba lagi di selang berikutnya */
      }
    }
    void cek()
    const id = setInterval(() => void cek(), SELANG_MS)
    return () => {
      batal = true
      clearInterval(id)
    }
  }, [fase, outletId, token, orderId])

  if (fase === "lunas")
    return (
      <Pusat>
        <Berhasil />
        <JudulPusat>Pembayaran lunas</JudulPusat>
        <p className="text-sm text-muted">Terima kasih! Pesananmu sedang disiapkan.</p>
        <Button fullWidth size="lg" onPress={onSelesai}>
          Selesai
        </Button>
      </Pusat>
    )

  return (
    <Pusat>
      <JudulPusat>Bayar QRIS</JudulPusat>
      {fase === "memuat" && <Memuat>Membuat kode QRIS…</Memuat>}
      {fase === "galat" && (
        <>
          <p className="text-sm text-danger-soft-foreground">{galat ?? "Gagal membuat QRIS"}</p>
          <Button fullWidth size="lg" onPress={onSelesai}>
            Kembali
          </Button>
        </>
      )}
      {fase === "tampil" && qrImg && (
        <>
          <p className="text-3xl leading-9 font-semibold text-aksen-teks tabular-nums">
            {rupiah(nominal)}
          </p>
          <img
            className="size-64 max-w-[80vw] rounded-xl border border-border"
            src={qrImg}
            alt="Kode QRIS"
            width={264}
            height={264}
          />
          <div className="flex items-center gap-2">
            <Spinner size="sm" />
            <span className="text-sm text-muted">Menunggu pembayaran…</span>
          </div>
          <p className="text-xs text-muted">
            Pindai dengan aplikasi bank/e-wallet apa pun. Jangan tutup halaman sampai lunas.
          </p>
          <Button fullWidth variant="ghost" onPress={onSelesai}>
            Bayar di kasir saja
          </Button>
        </>
      )}
    </Pusat>
  )
}
